/**
 * ContractorChangeOrders
 * ----------------------
 * A trade partner prices extra work and sends it up as a change order request.
 * The project manager approves it, which is what moves the contract value.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, FileText, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  draft: { cls: "bg-slate-100 text-slate-700 ring-slate-300", icon: Clock, label: "Draft" },
  submitted: { cls: "bg-amber-50 text-amber-700 ring-amber-300", icon: Clock, label: "Awaiting PM" },
  approved: { cls: "bg-emerald-50 text-emerald-700 ring-emerald-300", icon: CheckCircle2, label: "Approved" },
  rejected: { cls: "bg-red-50 text-red-700 ring-red-300", icon: XCircle, label: "Rejected" },
  void: { cls: "bg-slate-100 text-slate-500 ring-slate-300", icon: XCircle, label: "Void" },
};

const emptyItem = () => ({ description: "", qty: 1, uom: "LS", unit_price: 0 });

export default function ContractorChangeOrders({ token, proposalId, tradeName }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/change-orders/mine`, { headers });
      setRows(r.data?.change_orders || []);
      setProjects(r.data?.projects || []);
    } catch (e) {
      setProjects([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const project = projects.find((p) => p.source_quote?.id === proposalId) || projects[0] || null;
  const mine = rows.filter((c) => !project || c.project_id === project.id);

  const subtotal = (form?.line_items || []).reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0);

  const setItem = (i, key, v) => setForm((f) => ({
    ...f, line_items: f.line_items.map((li, j) => (j === i ? { ...li, [key]: v } : li)),
  }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Give your request a short title");
    setSaving(true);
    try {
      await axios.post(`${API}/change-orders`, {
        project_id: project.id,
        title: form.title,
        description: form.description,
        justification: form.justification,
        co_type: "owner_request",
        division_code: form.division_code,
        responsible_company: tradeName || "",
        schedule_impact_days: Number(form.schedule_impact_days || 0),
        line_items: form.line_items
          .filter((li) => li.description || li.unit_price)
          .map((li) => ({
            ...li, qty: Number(li.qty || 0), unit_price: Number(li.unit_price || 0),
            extended: Number(li.qty || 0) * Number(li.unit_price || 0),
          })),
      }, { headers });
      toast.success("Change order request sent to the project manager");
      setForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request");
    } finally { setSaving(false); }
  };

  if (loading || !project) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 mt-5" data-testid="contractor-change-orders">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">Change order requests</h3>
        {!form && (
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800"
            onClick={() => setForm({ title: "", description: "", justification: "", division_code: "", schedule_impact_days: 0, line_items: [emptyItem()] })}
            data-testid="contractor-new-co-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Request extra work
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Price work outside your original scope on <strong>{project.name}</strong>. Nothing changes the
        contract until the project manager approves it.
      </p>

      {mine.length > 0 && (
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg mb-3">
          {mine.map((co) => {
            const s = STATUS[co.status] || STATUS.draft;
            const Icon = s.icon;
            return (
              <div key={co.id} className="flex items-center justify-between gap-3 px-3 py-2.5 flex-wrap"
                data-testid={`contractor-co-${co.number}`}>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800">
                    <span className="font-mono text-xs text-slate-500 mr-2">{co.number}</span>{co.title}
                  </p>
                  <p className="text-xs text-slate-500">
                    {co.division_code || "—"}
                    {co.schedule_impact_days ? ` · +${co.schedule_impact_days} days` : ""}
                    {co.decision_note ? ` · PM: ${co.decision_note}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-black text-slate-900">{fmt$(co.total)}</span>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${s.cls}`}>
                    <Icon className="w-3 h-3" />{s.label}
                  </span>
                  <a href={`${API}/change-orders/${co.id}/pdf`} target="_blank" rel="noreferrer"
                    className="text-slate-400 hover:text-slate-700" title="Open PDF">
                    <FileText className="w-4 h-4" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-3">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input className="sm:col-span-2 bg-white" placeholder="Short title — e.g. Added anteroom exhaust at Room 4"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="contractor-co-title" />
            <Input className="bg-white" placeholder="Division (DIV 23 · HVAC)" value={form.division_code}
              onChange={(e) => setForm({ ...form, division_code: e.target.value })} />
          </div>
          <Textarea className="bg-white" rows={2} placeholder="What work is being added and why?"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="contractor-co-description" />
          {form.line_items.map((li, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
              <Input className="sm:col-span-6 bg-white" placeholder="Description of added work"
                value={li.description} onChange={(e) => setItem(i, "description", e.target.value)}
                data-testid={`contractor-co-line-${i}`} />
              <Input className="sm:col-span-1 bg-white" type="number" value={li.qty}
                onChange={(e) => setItem(i, "qty", e.target.value)} />
              <Input className="sm:col-span-1 bg-white" value={li.uom}
                onChange={(e) => setItem(i, "uom", e.target.value)} />
              <Input className="sm:col-span-2 bg-white" type="number" placeholder="Unit $" value={li.unit_price}
                onChange={(e) => setItem(i, "unit_price", e.target.value)} />
              <span className="sm:col-span-1 text-xs font-bold text-slate-700 text-right">
                {fmt$(Number(li.qty || 0) * Number(li.unit_price || 0))}
              </span>
              <Button size="icon" variant="ghost" className="sm:col-span-1"
                onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, j) => j !== i) })}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline"
                onClick={() => setForm({ ...form, line_items: [...form.line_items, emptyItem()] })}>
                <Plus className="w-3 h-3 mr-1" />Add line
              </Button>
              <Input className="w-40 bg-white" type="number" placeholder="Extra days"
                value={form.schedule_impact_days}
                onChange={(e) => setForm({ ...form, schedule_impact_days: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">Request total <b className="text-slate-900">{fmt$(subtotal)}</b></span>
              <Button size="sm" variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={submit} disabled={saving}
                data-testid="contractor-co-submit">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send to project manager
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
